import has from 'lodash/has';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import size from 'lodash/size';
import map from 'lodash/fp/map';
import flow from 'lodash/fp/flow';
import toPairs from 'lodash/fp/toPairs';
import fromPairs from 'lodash/fp/fromPairs';
import { gettext } from '@eb/i18n';
import { SubmissionError } from 'redux-form';
import { fetchJSON } from '@eb/http';
import { camelToSnake } from '@eb/string-utils';

import {
    EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    EMAIL_PERMISSION_DENIED_ERROR,
    TOKEN_NOT_AUTHORIZED_ERROR,
    EXTERNAL_USER_UNKNOWN_ERROR,
    INVALID_SOCIAL_LOGIN_FIELD,
    SUSPENDED_ACCOUNT,
    EXTERNAL_UNKNOWN_ERROR,
    USER_ALREADY_HAS_EXTERNAL_PROVIDER,
    STAFF_USER_NOT_ALLOWED_ERROR,
} from '../constants';

export const DEFAULT_ERROR_KEY = '__all__';

/*
    Our current policy allows for space to make up the entirety of a password,
    or contain space characters at either end so when password is passed through
    we need to exempt it.
*/
const _getTrimmedValue = (value, key) =>
    isString(value) && key !== 'password' ? value.trim() : value;

/**
 * Converts the keys of formData from camel to snake case and trims the values
 * @param {*} formData
 */
export const parseFormPostData = (formData) =>
    flow([
        toPairs,
        map(([key, value]) => [
            camelToSnake(key),
            _getTrimmedValue(value, key),
        ]),
        fromPairs,
    ])(formData);

/* istanbul ignore next */
const _hasInvalidResponse = (response) =>
    (has(response, 'success') && !response.success) ||
    (has(response, 'errors') && !isEmpty(response.errors));

export const CaptchaError = () => ({
    name: 'CAPTCHA_REQUIRED',
    message: 'Need captcha response',
});

export const ExternalUserWithoutEmailError = () => ({
    name: EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    message: EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
});

export const ExternalUserEmailAlreadyUsedError = () => ({
    name: EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    message: EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
});

export const StaffUserNotAllowedError = () => ({
    name: STAFF_USER_NOT_ALLOWED_ERROR,
    message: STAFF_USER_NOT_ALLOWED_ERROR,
});

export const ExternalUserEmailUsedOnSoftAccountError = () => ({
    name: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    message: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
});

export const EmailPermissionDeniedError = () => ({
    name: EMAIL_PERMISSION_DENIED_ERROR,
    message: EMAIL_PERMISSION_DENIED_ERROR,
});

export const TokenNotAuthorizedError = () => ({
    name: TOKEN_NOT_AUTHORIZED_ERROR,
    message: TOKEN_NOT_AUTHORIZED_ERROR,
});

export const ExternalUserUnknownError = () => ({
    name: EXTERNAL_USER_UNKNOWN_ERROR,
    message: EXTERNAL_USER_UNKNOWN_ERROR,
});

export const InvalidSocialLoginFieldError = () => ({
    name: INVALID_SOCIAL_LOGIN_FIELD,
    message: INVALID_SOCIAL_LOGIN_FIELD,
});

export const SuspendedAccountError = () => ({
    name: SUSPENDED_ACCOUNT,
    message: SUSPENDED_ACCOUNT,
});

export const UserAlreadyHasExternalProvider = () => ({
    name: USER_ALREADY_HAS_EXTERNAL_PROVIDER,
    message: USER_ALREADY_HAS_EXTERNAL_PROVIDER,
});

/* istanbul ignore next */
const _generateAuthenticationErrorObj = (errorSpec, errors) => {
    const errorObj = {};
    let checkedErrors = errors;

    //We only want to display global level errors if there are no
    //field specific errors. Authentication will only ever have server
    //field errors specific to email or password
    if (has(errors, DEFAULT_ERROR_KEY) && size(errors) > 1) {
        checkedErrors = omit(errors, DEFAULT_ERROR_KEY);
    }

    Object.keys(checkedErrors).forEach((key) => {
        //References implementation from ./authentication_form_mixin.js which also
        //assumes only 1 error ever in the array.
        const errorType = errors[key][0];
        let setterKey = key;

        if (key === DEFAULT_ERROR_KEY) {
            setterKey = '_error';
        }

        if (errorSpec[key]) {
            errorObj[setterKey] = errorSpec[key][errorType];
        }
    });

    if (isEmpty(errorObj)) {
        errorObj.type = EXTERNAL_UNKNOWN_ERROR;
    }

    return errorObj;
};

// eslint-disable complexity
// Throw especific error depending of error code.
export const getErrorObject = (errorSpec, responseErrorObj) => {
    if (
        responseErrorObj.captcha &&
        responseErrorObj.captcha[0] === 'CAPTCHA_REQUIRED'
    ) {
        throw new CaptchaError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
        )
    ) {
        // Scenario: facebookEmailNotProvided
        // handle special error case
        throw new ExternalUserWithoutEmailError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
        )
    ) {
        // Scenario: facebookSoftAccount
        // handle special error case
        throw new ExternalUserEmailUsedOnSoftAccountError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
        )
    ) {
        // Scenario: facebookEmailAlreadyUsed
        // handle special error case
        throw new ExternalUserEmailAlreadyUsedError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            EMAIL_PERMISSION_DENIED_ERROR,
        )
    ) {
        // Scenario: facebookEmailDenied
        // handle special error case
        throw new EmailPermissionDeniedError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            STAFF_USER_NOT_ALLOWED_ERROR,
        )
    ) {
        // Scenario: staffUserNotAllowed
        // handle special error case
        throw new StaffUserNotAllowedError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            TOKEN_NOT_AUTHORIZED_ERROR,
        )
    ) {
        // Scenario: facebookTokenNotAuthorized
        // handle special error case
        throw new TokenNotAuthorizedError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            EXTERNAL_USER_UNKNOWN_ERROR,
        )
    ) {
        // Scenario: facebookUnknownError
        // handle special error case
        throw new ExternalUserUnknownError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            INVALID_SOCIAL_LOGIN_FIELD,
        )
    ) {
        // Scenario: facebookInvalidField
        // handle special error case
        throw new InvalidSocialLoginFieldError();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(
            responseErrorObj[DEFAULT_ERROR_KEY],
            USER_ALREADY_HAS_EXTERNAL_PROVIDER,
        )
    ) {
        // Scenario: facebookalreadylinked
        // handle special error case
        throw new UserAlreadyHasExternalProvider();
    } else if (
        responseErrorObj[DEFAULT_ERROR_KEY] &&
        includes(responseErrorObj[DEFAULT_ERROR_KEY], SUSPENDED_ACCOUNT)
    ) {
        // handle special error case
        throw new SuspendedAccountError();
    } else {
        throw new SubmissionError(
            _generateAuthenticationErrorObj(errorSpec, responseErrorObj),
        );
    }
};

/* API to call diferent user actions: LogIn, Signup, Mail Soft Account, Social Action*/
export const fetchAuthentication = (
    errorSpec,
    url,
    { headers, method, ...options } = {},
) => {
    let fetchHeaders = headers;

    if (method && method !== 'GET') {
        fetchHeaders = {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            ...headers,
        };
    }

    const fetchOptions = {
        headers: fetchHeaders,
        method: method || 'GET',
        ...options,
    };

    return (
        fetchJSON(url, fetchOptions)
            //Need this catch to handle disconnected/general errors that may occur outside
            //of formed server responses
            .catch(() => {
                throw new SubmissionError({
                    _error: gettext(
                        'There was a problem with the server, try again',
                    ),
                });
            })
            .then((response) => {
                if (_hasInvalidResponse(response)) {
                    const responseErrorObj = omit(
                        response.errors,
                        'auth_backend',
                    );

                    getErrorObject(errorSpec, responseErrorObj);
                }

                return response;
            })
            .catch((response) => {
                throw response;
            })
    );
};
